/**
 * React Hooks for Institution Forms
 */

'use client';

import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { gatedAction } from '@/lib/api/auth-gate';
import type {
  InstitutionFormsListResponse,
  InstitutionFormsFilters
} from './types';

/**
 * Cache key generator for institution forms
 */
export function getInstitutionFormsCacheKey(
  matterId: number,
  institutionId: number,
  filters?: InstitutionFormsFilters
) {
  return ['institution-forms', matterId, institutionId, filters];
}

/**
 * Hook to fetch institution forms
 */
export function useInstitutionForms(
  matterId: number,
  institutionId: number | undefined,
  filters?: InstitutionFormsFilters,
  options?: Omit<
    UseQueryOptions<InstitutionFormsListResponse>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery({
    queryKey: getInstitutionFormsCacheKey(
      matterId,
      institutionId || 0,
      filters
    ),
    queryFn: async () => {
      if (!institutionId) {
        return { forms: [], total: 0 };
      }

      // Use server action for proper authentication
      const { getInstitutionFormsAction } = await import('./actions');
      return gatedAction(() =>
        getInstitutionFormsAction(matterId, institutionId, filters)
      );
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!matterId && !!institutionId,
    ...options
  });
}
